// Enhanced Pipeline Process API Interfaces
// Supports the new unified /v1/pipeline/process endpoint with optional fields and auto-detection

export interface RecoAndInsightsConfig {
  service_token?: string;
  source_pilot_id?: string;
}

export interface NbiGenerateConfigs {
  insightPath?: string;
  actionPath?: string;
  outputS3BasePath?: string;
  locale?: string;
  stringResourcesTagsList?: string;
  operationType?: string;
}

export interface NbiPopulateConfigs {
  insightPath?: string;
  actionPath?: string;
  locale?: string;
  stringResourcesTagsList?: string;
}

export interface NbiConfig {
  generate_configs?: NbiGenerateConfigs;
  populate_configs?: NbiPopulateConfigs;
}

export interface HybridConfig {
  v1_fuels?: string[];
  v2_fuels?: string[];
}

export interface ShcConfig {
  // No payload required - can be empty object
}

export interface PipelineProcessComponents {
  [componentKey: string]: any; // Dynamic component keys with flexible payload structure
}

export interface PipelineProcessRequest {
  projectId: string;
  components: PipelineProcessComponents;
}

export interface PipelineProcessResponse {
  success: boolean;
  data?: {
    projectId: string;
    status: string;
    components?: {
      reco_and_insights?: {
        status: string;
        message?: string;
        auto_detected?: {
          source_pilot_id?: string;
          service_token?: string;
        };
      };
      nbi?: {
        status: string;
        message?: string;
        default_configs_used?: boolean;
      };
      hybrid?: {
        status: string;
        message?: string;
        v1_fuels_processed?: string[];
        v2_fuels_processed?: string[];
      };
      shc?: {
        status: string;
        message?: string;
      };
    };
  };
  error?: string;
}

// Default configuration values
export const DEFAULT_NBI_CONFIG = {
  generate_configs: {
    locale: "en_US",
    stringResourcesTagsList: "her_ui,LP_COMPONENT_HER",
    operationType: "INSERT"
  },
  populate_configs: {
    locale: "en_US",
    stringResourcesTagsList: "her_ui,LP_COMPONENT_HER"
  }
};

// Auto-detection logic for source pilot ID
export const getAutoDetectedSourcePilotId = (customerType: string): string => {
  const lowerCustomerType = customerType.toLowerCase();
  
  if (lowerCustomerType.includes("residential") || lowerCustomerType.includes("resi")) {
    return "MASTER_RESIDENTIAL";
  }
  
  if (lowerCustomerType.includes("smb")) {
    return "MASTER_SMB";
  }
  
  // Default fallback
  return "MASTER_RESIDENTIAL";
};

// Centralized component key mappings
export const COMPONENT_KEYS = {
  RECO_AND_INSIGHTS: 'Insights_and_Recommendations',
  HYBRID: 'hybrid',
  NBI: 'NBI',
  SHC: 'SHC',
  RATE_COMPARISON: 'RATE_COMPARISON',
  TOU: 'TOU',
} as const;

// Component key mapping for known component types (for backward compatibility and name matching)
export const COMPONENT_KEY_MAPPINGS = {
  'reco-and-insights': COMPONENT_KEYS.RECO_AND_INSIGHTS,
  'insights': COMPONENT_KEYS.RECO_AND_INSIGHTS,
  'recommendations': COMPONENT_KEYS.RECO_AND_INSIGHTS,
  'nbi': COMPONENT_KEYS.NBI,
  'hybrid': COMPONENT_KEYS.HYBRID,
  'hybrid-models': COMPONENT_KEYS.HYBRID,
  'shc': COMPONENT_KEYS.SHC,
  'similar-home-comparison': COMPONENT_KEYS.SHC,
  'rate-comparison': COMPONENT_KEYS.RATE_COMPARISON,
} as const;

// Helper function to get component key from component name or id
export const getComponentKey = (component: { name: string; id: string; component_key?: string }): string => {
  // If component_key is explicitly provided, use it
  if (component.component_key) {
    return component.component_key;
  }
  
  // Otherwise, try to map from name or id
  const searchTerm = `${component.name} ${component.id}`.toLowerCase();

  for (const [key, value] of Object.entries(COMPONENT_KEY_MAPPINGS)) {
    if (searchTerm.includes(key)) {
      return value;
    }
  }
  
  // Default fallback - use the id as the key
  return component.id;
};

// Helper function to determine if a component supports auto-detection
export const supportsAutoDetection = (componentKey: string): boolean => {
  const autoDetectionKeys: string[] = [
    COMPONENT_KEYS.RECO_AND_INSIGHTS, 
    COMPONENT_KEYS.NBI, 
    COMPONENT_KEYS.HYBRID, 
    COMPONENT_KEYS.SHC,
    COMPONENT_KEYS.RATE_COMPARISON
  ];
  return autoDetectionKeys.includes(componentKey);
};

// Helper function to create minimal payload for a component
export const createMinimalPayload = (componentKey: string): any => {
  switch (componentKey) {
    case COMPONENT_KEYS.RECO_AND_INSIGHTS:
      return {}; // Auto-detection for source_pilot_id and service_token
    case COMPONENT_KEYS.NBI:
      return {}; // Auto-detection for S3 paths and configurations
    case COMPONENT_KEYS.HYBRID:
      return { v1_fuels: [], v2_fuels: [] }; // Empty arrays are allowed
    case COMPONENT_KEYS.SHC:
      return {}; // No payload required
    case COMPONENT_KEYS.RATE_COMPARISON:
      return { rate_plans_comparison_map: {} }; // Rate plans comparison map is required (validated in UI)
    default:
      return {}; // Default empty payload
  }
};
